import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {
  FlowchartTaskGroupLineageProcess,
  PROCESS_FLOWCHART_TASK_GROUP_MODIFICATION_SOURCE_DATA_QUERY,
} from 'processes/modals/ProcessFlowchartTaskGroupLineageQuery';

import {ProcessTaskDefinitionGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import type {
  ProcessFlowchartTaskGroupModificationSourceDataQuery,
  ProcessFlowchartTaskGroupModificationSourceDataQueryVariables,
} from '../../../../modals/__generated/ProcessFlowchartTaskGroupModificationSourceDataQuery';
import buildStaticDataFromFlowchartConfigGraphQL from '../buildStaticDataFromFlowchartConfigGraphQL';
import {
  isFlowchartConfigWithValidSchemasAndTaskDefinition,
  PersistedSchemaMapperConfigForExecutionSpec,
  ProcessFlowchartConfigVersionGQL,
  StaticTemplateFlowchart,
  toPersistedSchemaMapperConfigsForExecutionSpec,
} from '../processFlowchartConfigEditorConstants';
import {
  IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT,
  IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT,
} from '../redux/reduceFlowchartConfig';
import {Transition} from '../types/transition/TransitionTypes';
import {useProcessFlowchartConfigWithDefinitionsQuery} from '../useProcessFlowchartConfigQuery';
import type {
  ExecutionFlowchartRootProcessQuery,
  ExecutionFlowchartRootProcessQueryVariables,
} from './__generated/ExecutionFlowchartRootProcessQuery';
import type {PCMProcessData} from './ExecutionFlowchartPaneTypes';

export interface NodeStatusCount {
  statusType: ProcessTaskStatusType;
  displayName: string;
  count: number;
}

export type StatusCountsByNodeConfigId = Record<string, Array<NodeStatusCount>>;

export type ProcessIdByNodeConfigId = Record<string, string>;

export type NodeUrlByNodeConfigId = Record<string, string>;

const EXECUTION_FLOWCHART_FOR_EDITOR_QUERY = gql`
  query ExecutionFlowchartForEditorQuery($flowchartId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processFlowchart(id: $flowchartId) {
      ... on ProcessFlowchartNode {
        ...ProcessFlowchartForEditorGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcessFlowchartForEditor').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ExecutionFlowchartForEditorData {
  processFlowchart:
    | NonNullable<ProcessFlowchartConfigVersionGQL['templateFlowchart']>
    | {__typename: 'InaccessibleInfoNode'; invalidId: string; canRequestAccess: boolean};
}

/**
 * Result of resolving a flowchart config (and optional execution flowchart) into static template data
 * plus schema references. Built by `useFlowchartConfigData` or equivalent parent-side wiring.
 */
export interface LoadedFlowchartData {
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartTaskDefinitionId: string;
  flowchartConfigId: string;
  initialStaticFlowchart: StaticTemplateFlowchart;
  initialTransitions: Record<string, Transition>;
  hasChainedRouters: boolean;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  /** Task definitions for available schemas, fetched as part of the flowchart config version. */
  availableTaskDefinitions: Array<ProcessTaskDefinitionGraphQL>;
  /** From `ProcessFlowchartConfigVersionNode` (config metadata); defaults match settings editor. */
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
  /**
   * Persisted `ProcessFlowchartSchemaMapperConfig` references for the current flowchart config
   * version, flattened to the minimum shape execution save flows need: the persisted backend API
   * id plus the PTDV pair (and mapsToType) used to match drawn transitions back to a config.
   *
   * Empty when the version is missing/inaccessible (e.g. brand new draft configs).
   */
  persistedSchemaMapperConfigs: ReadonlyArray<PersistedSchemaMapperConfigForExecutionSpec>;
  /** The `wffcv_*` version ID from the Apollo cache — used to detect staleness against Redux `currentVersionId`. */
  flowchartConfigVersionId: string | null;
}

export enum FlowchartDataResultType {
  ERROR = 'ERROR',
  INACCESSIBLE = 'INACCESSIBLE',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
}

export type FlowchartDataResult =
  | {resultType: FlowchartDataResultType.ERROR}
  | {resultType: FlowchartDataResultType.INACCESSIBLE}
  | {resultType: FlowchartDataResultType.PENDING}
  | {resultType: FlowchartDataResultType.SUCCESS; data: LoadedFlowchartData};

const FLOWCHART_DATA_RESULT_PENDING: FlowchartDataResult = {resultType: FlowchartDataResultType.PENDING};
const FLOWCHART_DATA_RESULT_ERROR: FlowchartDataResult = {resultType: FlowchartDataResultType.ERROR};
const FLOWCHART_DATA_RESULT_INACCESSIBLE: FlowchartDataResult = {
  resultType: FlowchartDataResultType.INACCESSIBLE,
};

/** Resolves {@link LoadedFlowchartData} from flowchart config + optional execution flowchart queries. */
export function useFlowchartConfigData(
  flowchartConfigId: string | null,
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator | null,
  editorMode: FlowchartEditorMode,
  executionFlowchartId?: string | null,
  flowchartConfigVersionId?: string | null,
  networkOnly?: boolean
): FlowchartDataResult {
  const configQueryResult = useProcessFlowchartConfigWithDefinitionsQuery(
    flowchartConfigId,
    flowchartConfigVersionId,
    {
      networkOnly,
    }
  );

  const executionFlowchartResult = useSkippableBenchlingQuery<
    ExecutionFlowchartForEditorData,
    {flowchartId: string}
  >(EXECUTION_FLOWCHART_FOR_EDITOR_QUERY, {
    variables: {flowchartId: executionFlowchartId ?? ''},
    skip: executionFlowchartId == null,
  });

  const configQueryType = configQueryResult.type;
  const configProcessFlowchartConfig =
    configQueryType === QueryResultType.SUCCESS ? configQueryResult.data.processFlowchartConfig : null;
  const executionFlowchartQueryType = executionFlowchartResult.type;
  const executionProcessFlowchart =
    executionFlowchartQueryType === QueryResultType.SUCCESS
      ? executionFlowchartResult.data.processFlowchart
      : null;

  return useMemo((): FlowchartDataResult => {
    if (flowchartEditorConfigurator == null) {
      return FLOWCHART_DATA_RESULT_PENDING;
    }
    if (configQueryType === QueryResultType.PENDING || configQueryType === QueryResultType.SKIPPED) {
      return FLOWCHART_DATA_RESULT_PENDING;
    }
    if (configQueryType === QueryResultType.ERROR) {
      return FLOWCHART_DATA_RESULT_ERROR;
    }
    const configGQL = configProcessFlowchartConfig;
    if (
      configGQL == null ||
      isInaccessible(configGQL) ||
      !isFlowchartConfigWithValidSchemasAndTaskDefinition(configGQL)
    ) {
      return FLOWCHART_DATA_RESULT_INACCESSIBLE;
    }

    const rootTaskSchema = configGQL.schema;
    const flowchartConfigVersion = configGQL.flowchartConfigVersion;
    const resolvedVersion =
      flowchartConfigVersion != null && !isInaccessible(flowchartConfigVersion)
        ? flowchartConfigVersion
        : null;
    const availableTaskSchemas =
      resolvedVersion != null ? accessibleObjs(resolvedVersion.availableSchemas ?? []) : [];
    const childTaskDefinitions =
      resolvedVersion != null ? accessibleObjs(resolvedVersion.availableTaskDefinitions ?? []) : [];
    const availableTaskDefinitions = [configGQL.taskDefinition, ...childTaskDefinitions];

    let versionForBuilder = resolvedVersion;
    if (executionFlowchartId != null) {
      if (executionFlowchartQueryType === QueryResultType.ERROR) {
        return FLOWCHART_DATA_RESULT_ERROR;
      }
      if (
        executionFlowchartQueryType === QueryResultType.PENDING ||
        executionFlowchartQueryType === QueryResultType.SKIPPED
      ) {
        return FLOWCHART_DATA_RESULT_PENDING;
      }
      if (executionProcessFlowchart == null || isInaccessible(executionProcessFlowchart)) {
        return FLOWCHART_DATA_RESULT_INACCESSIBLE;
      }
      if (resolvedVersion != null) {
        versionForBuilder = {
          ...resolvedVersion,
          templateFlowchart: executionProcessFlowchart,
        };
      }
    }

    const {nodesStaticData, edgesStaticData, transitions, hasChainedRouters} =
      buildStaticDataFromFlowchartConfigGraphQL({
        flowchartConfigVersion: versionForBuilder,
        rootTaskSchema,
        rootTaskDefinition: configGQL.taskDefinition,
        allTaskSchemas: [rootTaskSchema, ...availableTaskSchemas],
        flowchartEditorConfigurator,
        editorMode,
      });

    const isFlowchartModifiableOnCreation =
      resolvedVersion?.isFlowchartModifiableOnCreation ?? IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT;
    const isFlowchartModifiableAfterCreation =
      resolvedVersion?.isFlowchartModifiableAfterCreation ?? IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT;

    const persistedSchemaMapperConfigs = toPersistedSchemaMapperConfigsForExecutionSpec(
      resolvedVersion?.schemaMapperConfigs ?? []
    );

    const success: FlowchartDataResult = {
      resultType: FlowchartDataResultType.SUCCESS,
      data: {
        rootTaskSchema,
        flowchartTaskDefinitionId: configGQL.taskDefinition.id,
        flowchartConfigId: configGQL.id,
        initialStaticFlowchart: {nodesStaticData, edgesStaticData},
        initialTransitions: transitions,
        hasChainedRouters,
        availableTaskSchemas,
        availableTaskDefinitions,
        isFlowchartModifiableOnCreation,
        isFlowchartModifiableAfterCreation,
        persistedSchemaMapperConfigs,
        flowchartConfigVersionId: resolvedVersion?.id ?? null,
      },
    };
    return success;
  }, [
    configQueryType,
    configProcessFlowchartConfig,
    executionFlowchartQueryType,
    executionProcessFlowchart,
    flowchartEditorConfigurator,
    editorMode,
    executionFlowchartId,
  ]);
}

/**
 * Lighter query for execution flowchart resolution from a root process that avoids
 * loading the task schema's `currentFlowchartConfigVersion` (and its schema mapper
 * configs). The pinned config version is loaded separately via
 * `useProcessFlowchartConfigQuery`. This query provides the `flowchartConfig.id`,
 * `tasks[].executionFlowchart.id`, and `flowchartConfigVersion.id` needed by
 * `useExecutionFlowchartDraftStateForProcess`.
 */
const EXECUTION_FLOWCHART_ROOT_PROCESS_QUERY = gql`
  query ExecutionFlowchartRootProcessQuery($flowchartProcessId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    process(id: $flowchartProcessId) {
      ... on ProcessNode {
        id
        taskSchemaOrInaccessible {
          ... on ProcessTaskSchemaNode {
            ...ProcessTaskSchemaWithoutFlowchartConfigGraphQL
            flowchartConfig {
              ... on ProcessFlowchartConfigNode {
                id
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
        tasks {
          ... on ProcessTaskNode {
            id
            name
            executionFlowchart {
              id
            }
          }
        }
        flowchartConfigVersion {
          ... on ProcessFlowchartConfigVersionNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('ProcessTaskSchemaWithoutFlowchartConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/** Loads root process + task schema for PCM-style execution draft resolution; `undefined` while loading. */
export function useProcessData(processId: string | null): PCMProcessData | null | undefined {
  const result = useSkippableBenchlingQuery<
    ExecutionFlowchartRootProcessQuery,
    ExecutionFlowchartRootProcessQueryVariables
  >(EXECUTION_FLOWCHART_ROOT_PROCESS_QUERY, {
    variables: {flowchartProcessId: processId ?? ''},
    skip: processId == null,
  });

  return useMemo(() => {
    if (processId == null) {
      return null;
    }
    if (result.type === QueryResultType.PENDING || result.type === QueryResultType.SKIPPED) {
      return undefined;
    }
    if (result.type === QueryResultType.ERROR || isInaccessible(result.data.process)) {
      return null;
    }
    return result.data.process;
  }, [processId, result]);
}

/** Derives per-node-config status counts, process ID mappings, and node URLs from task-group lineage in a single pass. */
export function buildLineageMapsFromRootProcess(rootProcess: FlowchartTaskGroupLineageProcess): {
  statusCountsByNodeConfigId: StatusCountsByNodeConfigId;
  processIdsByNodeConfigId: ProcessIdByNodeConfigId;
  nodeUrlsByNodeConfigId: NodeUrlByNodeConfigId;
} {
  const statusCountsByNodeConfigId: StatusCountsByNodeConfigId = {};
  const processIdsByNodeConfigId: ProcessIdByNodeConfigId = {};
  const nodeUrlsByNodeConfigId: NodeUrlByNodeConfigId = {};
  const allProcesses = [rootProcess, ...rootProcess.childProcesses];
  for (const process of allProcesses) {
    if (isInaccessible(process) || process.nodeConfig == null) {
      continue;
    }
    if (process.taskStatusCounts.length === 0) {
      continue;
    }
    statusCountsByNodeConfigId[process.nodeConfig.id] = process.taskStatusCounts.map((statusCount) => ({
      statusType: statusCount.status.statusType,
      displayName: statusCount.status.displayName,
      count: statusCount.count,
    }));
    processIdsByNodeConfigId[process.nodeConfig.id] = process.id;
    if (process.flowchartNodeUrl != null) {
      nodeUrlsByNodeConfigId[process.nodeConfig.id] = process.flowchartNodeUrl;
    }
  }
  return {statusCountsByNodeConfigId, processIdsByNodeConfigId, nodeUrlsByNodeConfigId};
}

interface UseTaskGroupLineageDataArgs {
  flowchartId: string | null;
  flowchartProcessId: string | null;
  nodeTaskSchemaIds: Array<string>;
  /** When set, scopes taskStatusCounts and flowchartPredecessorNodes to this flowchart root task's lineage. */
  lineageRootTaskId?: string;
}

export enum LineageDataResultType {
  PENDING = 'PENDING',
  UNAVAILABLE = 'UNAVAILABLE',
  SUCCESS = 'SUCCESS',
}

export type LineageDataResult =
  | {resultType: LineageDataResultType.PENDING}
  | {resultType: LineageDataResultType.UNAVAILABLE}
  | {
      resultType: LineageDataResultType.SUCCESS;
      data: StatusCountsByNodeConfigId;
      processIdsByNodeConfigId: ProcessIdByNodeConfigId;
      nodeUrlsByNodeConfigId: NodeUrlByNodeConfigId;
    };

const LINEAGE_DATA_RESULT_PENDING: LineageDataResult = {resultType: LineageDataResultType.PENDING};
const LINEAGE_DATA_RESULT_UNAVAILABLE: LineageDataResult = {resultType: LineageDataResultType.UNAVAILABLE};

/** Status counts for flowchart nodes, derived from task-group modification lineage when ids are known. */
export function useTaskGroupLineageData(args: UseTaskGroupLineageDataArgs): LineageDataResult {
  const {flowchartId, flowchartProcessId, nodeTaskSchemaIds, lineageRootTaskId} = args;
  const skip = flowchartId == null || flowchartProcessId == null;
  const result = useSkippableBenchlingQuery<
    ProcessFlowchartTaskGroupModificationSourceDataQuery,
    ProcessFlowchartTaskGroupModificationSourceDataQueryVariables
  >(PROCESS_FLOWCHART_TASK_GROUP_MODIFICATION_SOURCE_DATA_QUERY, {
    variables: {
      flowchartId: flowchartId ?? '',
      flowchartProcessId: flowchartProcessId ?? '',
      nodeTaskSchemaIds,
      rootTaskId: lineageRootTaskId,
    },
    skip,
  });

  const resultType = result.type;
  const lineageRootProcess = resultType === QueryResultType.SUCCESS ? result.data.process : null;

  return useMemo((): LineageDataResult => {
    if (skip) {
      return LINEAGE_DATA_RESULT_UNAVAILABLE;
    }
    if (resultType === QueryResultType.PENDING || resultType === QueryResultType.SKIPPED) {
      return LINEAGE_DATA_RESULT_PENDING;
    }
    if (resultType === QueryResultType.ERROR) {
      return LINEAGE_DATA_RESULT_UNAVAILABLE;
    }
    if (lineageRootProcess == null || isInaccessible(lineageRootProcess)) {
      return LINEAGE_DATA_RESULT_UNAVAILABLE;
    }
    const {statusCountsByNodeConfigId, processIdsByNodeConfigId, nodeUrlsByNodeConfigId} =
      buildLineageMapsFromRootProcess(lineageRootProcess);
    return {
      resultType: LineageDataResultType.SUCCESS,
      data: statusCountsByNodeConfigId,
      processIdsByNodeConfigId,
      nodeUrlsByNodeConfigId,
    };
  }, [skip, resultType, lineageRootProcess]);
}
